import { SimilarListing } from '@zoocasa/go-search';
import { ADDRESS_NOT_AVAILABLE_LABEL, SQFT_NOT_AVAILABLE_LABEL } from 'components/listing-card/listing-card-v2';
import { parseWktPoint } from 'components/search-es/geo_helper';
import { cleanDashedString } from 'data/addresses';
import { isExpired, isSold } from 'types/reso';
import { getProviderLogoUrl } from 'utils/compliance';
import generateFullAddress from 'utils/get-full-address';
import { isUsStateCode, provinceOrStateCodeFromString } from 'utils/province_or_state';
import { isQuebecListing } from 'utils/is-quebec-listing';

import type { ComplianceData, SimilarListing as SimilarListingType } from './components/similar-listings/similar-listings.type';

export class SimilarListingViewModel implements SimilarListingType {
  constructor(
    private listing: SimilarListing, private complianceData: ComplianceData
  ) { /* no-init */ }

  get compliance() {
    return this.complianceData.compliance;
  }

  get providerId() {
    return this.listing.providerId;
  }

  get providerLogo() {
    /* At the moment, we only have the provider logo for US listings */
    if (isUsStateCode(provinceOrStateCodeFromString(this.listing?.province))) {
      return getProviderLogoUrl(this.listing.providerId);
    }
    return null;
  }

  get brokerage() {
    return this.listing.brokerage;
  }

  get mlsNum() {
    return this.listing.mls;
  }

  get position() {
    const pointWkt = this.listing.position; // SimilarListing position is stored as a WKT string POINT (longitude, latitude)
    if (pointWkt) {
      const { latitude, longitude } = parseWktPoint(pointWkt);
      return {
        latitude,
        longitude,
      };
    }
    return null;
  }

  get href() {
    if (!this.listing.displayAddress) {
      return this.listing.addressPath;
    }
    return cleanDashedString(`/${this.listing.addressPath}`);
  }

  get fullAddress() {
    const fullAddress = generateFullAddress(this.street, this.city, this.province, false);
    if (fullAddress) {
      return fullAddress;
    }
    return ADDRESS_NOT_AVAILABLE_LABEL;
  }

  get street() {
    const { streetNumber, streetName, unit } = this.listing;
    const streetAddress = streetNumber ? `${streetNumber} ${streetName}` : streetName;
    const fullAddress = unit?.length > 0 ? `${unit} - ${streetAddress}` : streetAddress;
    return fullAddress;
  }

  get city() {
    return this.listing.subDivision;
  }

  get province() {
    return this.listing.province;
  }
  get timestamp() {
    // Quebec listings must not display timestamps for regional compliance
    if (isQuebecListing(this.listing.province)) {
      return null;
    }
    
    if (isSold(this.listing.standardStatus)) {
      return this.listing.soldAt || null;
    } else if (isExpired(this.listing.standardStatus)) {
      return this.listing.updatedOn || this.listing.createdAt || null;
    }
    return this.listing.createdAt || null;
  }

  get id() {
    return this.listing.id;
  }

  get thumbnailSrc() {
    if (this.hasImageRootStorageKey()) {
      if (this.hasImageSource()) {
        return this.listing.imageRootStorageKey;
      }
      return `https://cdn0.zoocasa.com/${this.listing.imageRootStorageKey}.jpg`;
    }
    return '';
  }

  get thumbnailAlt() {
    return this.street;
  }

  get price() {
    return this.listing.price;
  }

  get soldPrice() {
    return this.listing.soldPrice;
  }

  get standardStatus() {
    return this.listing.standardStatus;
  }

  get bedrooms() {
    return this.listing.bedrooms;
  }

  get bathrooms() {
    return this.listing.bathrooms;
  }

  get avgSqft() {
    let avgSqft: number;
    if (this.listing.squareFootage) {
      const { gt, gte, lt, lte } = this.listing.squareFootage;
      const min = lt || lte;
      const max = gt || gte;
      if (min && max) {
        avgSqft = Math.ceil((min + max) / 2);
      } else if (min) {
        avgSqft = min;
      } else if (max) {
        avgSqft = max;
      }
    }
    return avgSqft;
  }

  get sqftLabel() {
    let sqftLabel = '';
    if (this.listing.squareFootage) {
      const { gt, gte, lt, lte } = this.listing.squareFootage;
      const min = gt || gte;
      const max = lt || lte;
      let sqft: string = SQFT_NOT_AVAILABLE_LABEL;

      // Note: if max sqft is 499 always show 0-499, for anything else show min-max or just min or max when only one available
      sqft = max === 499 ? '0-499' : [min, max].filter(item => item).join('-');

      sqftLabel = !sqft?.trim()?.length ? SQFT_NOT_AVAILABLE_LABEL : sqft.trim();
    }
    return sqftLabel;
  }

  get isRental() {
    return this.listing.isRental;
  }

  private hasImageRootStorageKey() {
    return this.listing?.imageRootStorageKey.length > 0;
  }

  private hasImageSource(){
    return this.listing?.imageSource == 'exp';
  }
}